import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Pencil, Check, X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { InfoHint } from '@ooh/ui/components/info-hint';
import type { Role } from '@ooh/contracts';

export interface RoleDetailHeaderProps {
  role: Role;
  isImmutable: boolean;
  /** Translated group label for the selected role, if it belongs to one (e.g. "Kierownicy działów"). */
  groupLabel?: string;
  renaming: boolean;
  renameValue: string;
  onRenameValueChange: (value: string) => void;
  onStartRename: () => void;
  onConfirmRename: () => void;
  onCancelRename: () => void;
  onToggleActiveClick: () => void;
  /** True while the rename PATCH request is in flight — disables the rename input and confirm button. */
  renameSaving?: boolean;
}

/**
 * Top bar of the role detail panel: name (+ inline rename), code, immutable
 * lock badge, inactive tag, group label, and the deactivate/activate trigger.
 */
export function RoleDetailHeader({
  role,
  isImmutable,
  groupLabel,
  renaming,
  renameValue,
  onRenameValueChange,
  onStartRename,
  onConfirmRename,
  onCancelRename,
  onToggleActiveClick,
  renameSaving = false,
}: RoleDetailHeaderProps) {
  const { t } = useTranslation('admin');

  return (
    <div className="px-8 py-3.5 border-b border-border/40 flex items-center gap-3 shrink-0">
      {renaming ? (
        <div className="flex items-center gap-2 flex-1">
          <Input
            value={renameValue}
            onChange={(e) => onRenameValueChange(e.target.value)}
            className="h-8 max-w-xs text-sm"
            autoFocus
            disabled={renameSaving}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onConfirmRename();
              if (e.key === 'Escape') onCancelRename();
            }}
          />
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={onConfirmRename}
            disabled={renameSaving}
            aria-label={t('permissionMatrix.roleHeader.renameSaveAriaLabel')}
          >
            <Check className="w-4 h-4" />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={onCancelRename}
            disabled={renameSaving}
            aria-label={t('permissionMatrix.roleHeader.renameCancelAriaLabel')}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <h2 className="text-[15px] font-semibold truncate" title={role.name}>
            {role.name}
          </h2>
          <span className="font-mono text-2xs text-muted-foreground shrink-0">{role.code}</span>
          {isImmutable ? (
            <span className="inline-flex items-center gap-1 text-2xs font-medium text-primary shrink-0">
              <Lock className="w-3 h-3" />
              {t('permissionMatrix.roleHeader.immutableBadge')}
              <InfoHint label={t('permissionMatrix.roleHeader.immutableInfoLabel')} iconSize={12}>
                {t('permissionMatrix.roleHeader.immutableInfoText')}
              </InfoHint>
            </span>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onStartRename}
              aria-label={t('permissionMatrix.roleHeader.renameButtonAriaLabel')}
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>
          )}
          {!role.isActive && (
            <span className="text-2xs text-muted-foreground shrink-0">
              {t('permissionMatrix.roleHeader.inactiveTag')}
            </span>
          )}
          {groupLabel ? (
            <span className="text-2xs text-muted-foreground/70 shrink-0 ml-1">{groupLabel}</span>
          ) : null}
        </div>
      )}
      {!isImmutable && !renaming && (
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleActiveClick}
          data-testid="toggle-active-btn"
        >
          {role.isActive
            ? t('permissionMatrix.roleHeader.deactivateButton')
            : t('permissionMatrix.roleHeader.activateButton')}
        </Button>
      )}
    </div>
  );
}
